import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { shortAddress, etherscanUrl } from 'dibs-crypto-utils/exports/formattingUtils';
import { getAuctionContractAddress } from 'dibs-crypto-utils/exports/getAuctionContractAddress';
import { isV2Auction } from 'dibs-crypto-utils/exports/isV2Auction';

import styles from './NftPdpUserLink.scss';
import { type NftPdpUserLink_ethereumAddress$key } from './__generated__/NftPdpUserLink_ethereumAddress.graphql';

const messages = defineMessages({
    currentUserAction: { id: 'nft.pdp.yourAction', defaultMessage: '{name} (You)' },
});

type Props = {
    nftEthereumAddress: NftPdpUserLink_ethereumAddress$key | null | undefined;
    userWalletAddress?: string | null;
};

export const NftPdpUserLink: FC<Props> = ({ nftEthereumAddress, userWalletAddress }) => {
    const { formatMessage } = useIntl();
    const { address, publicProfileUrl, publicProfile, isDibsAddress, ensDomain } =
        useFragment(
            graphql`
                fragment NftPdpUserLink_ethereumAddress on EthereumAddressType {
                    address
                    publicProfileUrl
                    publicProfile {
                        profileName
                    }
                    isDibsAddress
                    ensDomain
                }
            `,
            nftEthereumAddress
        ) ?? {};

    const { profileName } = publicProfile ?? {};

    const isAuctionContractAddress =
        address === getAuctionContractAddress() || isV2Auction(address);

    const isCurrentUser = address === userWalletAddress;

    if (isDibsAddress || isAuctionContractAddress) {
        return (
            <FormattedMessage
                id="nft.pdp.history.dibsMarketplace"
                defaultMessage="1stDibs Marketplace"
            />
        );
    } else if (profileName) {
        return (
            <Link
                href={publicProfileUrl || ''}
                className={styles.noWrap}
                key={`profile-link-${profileName}`}
            >
                {isCurrentUser
                    ? formatMessage(messages.currentUserAction, {
                          name: profileName,
                      })
                    : profileName}
            </Link>
        );
    } else if (ensDomain) {
        return (
            <Link
                href={etherscanUrl(`/address/${address}`)}
                target="_blank"
                className={styles.noWrap}
                key="auction-creator-link"
            >
                {isCurrentUser
                    ? formatMessage(messages.currentUserAction, {
                          name: ensDomain,
                      })
                    : ensDomain}
            </Link>
        );
    } else {
        return (
            <Link
                href={etherscanUrl(`/address/${address}`)}
                target="_blank"
                className={styles.noWrap}
                key="auction-creator-link"
            >
                {isCurrentUser
                    ? formatMessage(messages.currentUserAction, {
                          name: shortAddress(address || ''),
                      })
                    : shortAddress(address || '')}
            </Link>
        );
    }
};
